import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
  HeartOutlined,
  SearchOutlined,
  PlusOutlined
} from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Typography,
  notification,
  Card,
  Statistic
} from "antd";
import { useEffect, useState, useCallback } from "react";
import ajaxService from "../../../services/ajax-service";
import { Filter } from "../../../components";
import { Link } from "react-router-dom";
import moment from "moment";
import { calculateTableScrollX, calculateModalWidth } from "../../../utils/widthCalculator";

const { Text, Title } = Typography;

const Interests = () => {
  const [form] = Form.useForm();

  const [showModal, setShowModal] = useState(false);

  const [dataSource, setDataSource] = useState({ data: [], current_page: 1, per_page: 10, total: 0 });
  const [additionalData, setAdditionalData] = useState({
    total_interests: 0,
    categories: 0,
    this_month: 0
  });

  const [allInterests, setAllInterests] = useState([]);
  const [fullInterests, setFullInterests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const [editingInterest, setEditingInterest] = useState(null); // for edit mode

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [interestToDelete, setInterestToDelete] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [modalWidth, setModalWidth] = useState('35%');

  const pageSize = 10;

  const columns = [
    {
      title: "SNo",
      dataIndex: "id",
      key: "id",
      width: 70,
      fixed: 'left',
      render: (text, record, index) => (
        <Text style={{ fontSize: '0.85rem' }}>{(dataSource.current_page - 1) * dataSource.per_page + index + 1}</Text>
      ),
    },
    {
      title: "Interests",
      dataIndex: "name",
      key: "name",
      width: 350,
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (name) => <Text style={{ fontSize: '0.85rem' }}>{name}</Text>,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 110,
      sorter: (a, b) => (moment(a.created_at).unix() || 0) - (moment(b.created_at).unix() || 0),
      sortDirections: ['ascend', 'descend'],
      render: (created_at) => (<Text style={{ fontSize: '0.8rem' }}>{moment(created_at).format('DD/MM/YY')}</Text>)
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      fixed: 'right',
      render: (text, record) => (
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          <Button
            type="text"
            size="small"
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              color: 'var(--accent-purple)',
              border: '1px solid var(--accent-purple)',
              borderRadius: 'var(--radius-md)',
              transition: 'var(--transition-fast)'
            }}
            icon={<EditOutlined style={{ fontSize: '12px' }} />}
            onClick={() => openEditModal(record)}
          />
          <Button
            type="text"
            size="small"
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              color: 'var(--error-red)',
              border: '1px solid var(--error-red)',
              borderRadius: 'var(--radius-md)',
              transition: 'var(--transition-fast)'
            }}
            icon={<DeleteOutlined style={{ fontSize: '12px' }} />}
            onClick={() => handleDeleteClick(record.id)}
          />
        </div>
      ),
    },
  ];

  const handleDeleteClick = (id) => {
    setInterestToDelete(id);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (interestToDelete) {
      try {
        const response = await ajaxService.delete('/interest/', interestToDelete);
        if (response.success) {
          notification.open({
            message: `Interest deleted successfully!`,
            icon: <CheckCircleOutlined style={{ color: "var(--success-green)" }} />,
          });
          initialize();
        } else {
          notification.open({
            message: "Unable to delete interest.",
            icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
          });
        }
      } catch (error) {
        console.error('Delete error:', error);
        notification.open({
          message: "Unable to delete interest.",
            icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
        });
      }
    }
    setDeleteModalVisible(false);
    setInterestToDelete(null);
  };

  const onFinish = async (values) => {
    let response;
    if (editingInterest) {
      // update with FormData
     const payload = {
        name: values.name,
        type: 1,
      };
      response = await ajaxService.put('/interest/',editingInterest.id, payload);
    } else {
      // create
      const payload = {
        name: values.name,
        type: 1,
      };
      response = await ajaxService.post("/interest", payload);
    }

    if (response.success) {
      handleCancel();
      initialize();
      notification.open({
        message: response.message || (editingInterest ? "Interest updated successfully!" : "Interest added successfully!"),
        icon: <CheckCircleOutlined style={{ color: "var(--primary-purple)" }} />,
      });
    } else {
      notification.open({
        message: "Unable to save interest.",
            icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
      });
    }
  };

  const openEditModal = (record) => {
    setEditingInterest(record);
    setShowModal(true);
    form.setFieldsValue({
      name: record.name,
    });
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed: " + errorInfo);
  };

  const handleSearch = ({ name }) => {
    const query = name ? name.toLowerCase().trim() : '';
    if (!query) {
      setAllInterests(fullInterests);
      setCurrentPage(1);
      const start = 0;
      const end = pageSize;
      const sliced = fullInterests.slice(start, end);
      setDataSource({
        data: sliced,
        current_page: 1,
        per_page: pageSize,
        total: fullInterests.length
      });
      return;
    }

    const filtered = fullInterests.filter(interest =>
      interest.name.toLowerCase().includes(query)
    );

    if (filtered.length === 0) {
      notification.open({
        message: `${query} is not found!`,
            icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
      });
      setAllInterests(fullInterests);
      setCurrentPage(1);
      const start = 0;
      const end = pageSize;
      const sliced = fullInterests.slice(start, end);
      setDataSource({
        data: sliced,
        current_page: 1,
        per_page: pageSize,
        total: fullInterests.length
      });
      return;
    }

    setAllInterests(filtered);
    setCurrentPage(1);
    const start = 0;
    const end = pageSize;
    const sliced = filtered.slice(start, end);
    setDataSource({
      data: sliced,
      current_page: 1,
      per_page: pageSize,
      total: filtered.length
    });
  };

  const handleChangePage = (page) => {
    setCurrentPage(page);
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    const sliced = allInterests.slice(start, end);
    setDataSource({
      data: sliced,
      current_page: page,
      per_page: pageSize,
      total: allInterests.length
    });
  };

  const initialize = async () => {
    const response = await ajaxService.get("/interest");
    if (response.success) {
      const interests = response.data?.data || response.data || [];
      setFullInterests(interests);
      setAllInterests(interests);
      setCurrentPage(1);
      const start = 0;
      const end = pageSize;
      const sliced = interests.slice(start, end);
      setDataSource({
        data: sliced,
        current_page: 1,
        per_page: pageSize,
        total: interests.length
      });
      if (response.additionalData) setAdditionalData(response.additionalData);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  // Listen for sidebar collapse state
  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };

    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
  }, []);

  // Calculate table scroll width using standardized utility
  // Table columns total: 70 (SNo) + 350 (Interests) + 140 (Actions) = 560px
  const calculateTableWidth = useCallback(() => {
    return calculateTableScrollX(sidebarCollapsed, 560, 560, 40);
  }, [sidebarCollapsed]);

  const [tableWidth, setTableWidth] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      return calculateTableScrollX(false, 560, 560, 40);
    }
    return '100%';
  });

  // Recalculate on window resize and sidebar toggle
  useEffect(() => {
    const handleResize = () => {
      setTableWidth(calculateTableWidth());
      setModalWidth(calculateModalWidth('35%'));
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial calculation
    
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateTableWidth]);

  const handleCancel = () => {
    setShowModal(false);
    setEditingInterest(null);
    form.resetFields();
  };

  const renderModal = (
    <Modal
      className="modal-overlay"
      width={modalWidth}
      open={showModal}
      maskClosable={false}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      <div style={{ 
        padding: "var(--spacing-2xl) var(--spacing-xl)",
        background: 'var(--dark-bg-card)',
        borderRadius: 'var(--radius-lg)'
      }}>
        {/* Modal Header */}
        <div style={{ 
          marginBottom: 'var(--spacing-2xl)', 
          textAlign: "center",
          paddingBottom: 'var(--spacing-lg)',
          borderBottom: '1px solid rgba(254, 2, 125, 0.2)'
        }}>
          <Title id="modal-title" level={3} style={{ 
            color: 'var(--text-white)', 
            margin: 0,
            fontFamily: 'Poppins-Bold',
            fontSize: '1.5rem'
          }}>
            {editingInterest ? "Edit Interest" : "Add New Interest"}
          </Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ 
                  color: 'var(--text-white)', 
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'block',
                  marginBottom: 'var(--spacing-xs)'
                }}>Interest</span>}
                name="name"
                rules={[{ required: true, message: "Interest is required" }]}
              >
                <Input
                  addonBefore="Interest "
                  placeholder="Write interest here"
                  allowClear
                  className="input-field"
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Modal Footer */}
          <div style={{ 
            textAlign: "center", 
            marginTop: 'var(--spacing-2xl)',
            paddingTop: 'var(--spacing-lg)',
            borderTop: '1px solid rgba(254, 2, 125, 0.2)',
            display: 'flex',
            gap: 'var(--spacing-md)',
            justifyContent: 'center'
          }}>
            <Button
              key="cancel"
              onClick={handleCancel}
              style={{
                minWidth: '120px',
                height: '40px',
                background: 'var(--dark-bg-hover)',
                borderColor: 'rgba(254, 2, 125, 0.3)',
                color: 'var(--text-white)'
              }}
            >
              Cancel
            </Button>
            <Button
              key="finish"
              type="primary"
              onClick={form.submit}
              className="btn-primary"
              style={{ 
                minWidth: '120px',
                height: '40px'
              }}
            >
              {editingInterest ? "Update" : "Add"}
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Header Section */}
      <div className="module-header">
        <h1 className="module-title">Interest Management</h1>
        <p className="module-subtitle">Manage user interests and preferences</p>
      </div>

      {/* Statistics Cards - from API additionalData */}
      <div className="stats-grid">
        <div className="stats-card">
          <div className="stats-value">{additionalData.total_interests ?? 0}</div>
          <div className="stats-label">Total Interests</div>
          <HeartOutlined style={{ color: 'var(--primary-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
        <div className="stats-card">
          <div className="stats-value">{additionalData.categories ?? 0}</div>
          <div className="stats-label">Categories</div>
          <HeartOutlined style={{ color: 'var(--accent-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
        <div className="stats-card">
          <div className="stats-value">{additionalData.this_month ?? 0}</div>
          <div className="stats-label">This Month</div>
          <HeartOutlined style={{ color: 'var(--text-gray)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filter-section">
        <div className="filter-row">
          <div className="filter-item">
            <Input
              placeholder="Search interests..."
              prefix={<SearchOutlined style={{ color: 'var(--text-gray-light)' }} />}
              onPressEnter={(e) => handleSearch({ name: e.target.value })}
              className="input-field"
            />
          </div>
          <div className="filter-actions">
            <Button
              className="btn-primary"
              icon={<PlusOutlined />}
              onClick={() => setShowModal(true)}
            >
              Add New Interest
            </Button>
          </div>
        </div>
      </div>

      {/* Interests Table */}
      <div className={`data-table-card ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Table
          columns={columns}
          dataSource={dataSource.data || []}
          className="data-table"
          rowKey="id"
          pagination={{
            onChange: handleChangePage,
            current: dataSource.current_page,
            pageSize: dataSource.per_page,
            total: dataSource.total,
            className: "data-table-pagination",
            showSizeChanger: false,
            showQuickJumper: false,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} interests`,
            size: 'small'
          }}
          scroll={{ x: tableWidth }}
          size="small"
          bordered={false}
        />
      </div>

      {renderModal}
      <Modal
        className="modal-overlay"
        width={calculateModalWidth('400px')}
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        footer={null}
        centered
      >
        <div style={{ 
          padding: "var(--spacing-2xl)",
          background: 'var(--dark-bg-card)',
          borderRadius: 'var(--radius-lg)',
          textAlign: 'center'
        }}>
          <div style={{ 
            marginBottom: 'var(--spacing-xl)',
            fontSize: '3rem',
            color: 'var(--error-red)'
          }}>
            <DeleteOutlined />
          </div>
          <Title level={4} style={{ 
            color: 'var(--text-white)', 
            marginBottom: 'var(--spacing-md)',
            fontFamily: 'Poppins-Bold'
          }}>
            Confirm Delete
          </Title>
          <p style={{ 
            color: 'var(--text-gray)', 
            marginBottom: 'var(--spacing-xl)',
            fontSize: '0.95rem',
            lineHeight: 1.6
          }}>
            Are you sure you want to delete this interest? This action cannot be undone.
          </p>
          <div style={{ 
            display: 'flex',
            gap: 'var(--spacing-md)',
            justifyContent: 'center'
          }}>
            <Button
              onClick={() => setDeleteModalVisible(false)}
              style={{
                minWidth: '120px',
                height: '40px',
                background: 'var(--dark-bg-hover)',
                borderColor: 'rgba(254, 2, 125, 0.3)',
                color: 'var(--text-white)'
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              danger
              onClick={handleConfirmDelete}
              style={{
                minWidth: '120px',
                height: '40px',
                background: 'var(--error-red)',
                borderColor: 'var(--error-red)'
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Interests;